import { useState } from "react";
import { ChevronDown, ChevronUp, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

// ── Metric type catalogue ────────────────────────────────────────────────────
// Each entry defines the available timeframes and the backend KPI key each
// combination maps to.

const METRIC_TYPES = [
  {
    value: "arr",
    label: "ARR",
    description: "Annual recurring revenue — sum of monthly MRR across the financial year",
    timeframes: [
      { value: "this_fy", label: "This FY",         key: "arr_fy"       },
      { value: "last_fy", label: "Last FY",         key: "arr_last_fy"  },
      { value: "next_fy", label: "Next FY (proj.)", key: "arr_next_fy"  },
    ],
  },
  {
    value: "mrr",
    label: "MRR",
    description: "Monthly recurring revenue at the current month",
    timeframes: [
      { value: "current", label: "Current month", key: "current_mrr" },
    ],
  },
  {
    value: "closed_won",
    label: "Closed won",
    description: "Total deal amounts (all revenue) for closed-won deals",
    timeframes: [
      { value: "this_fy", label: "This FY",            key: "closed_won_amount_fy" },
      { value: "this_cy", label: "This calendar year", key: "closed_won_amount_cy" },
    ],
  },
  {
    value: "churned",
    label: "Churned",
    description: "Churned annual recurring revenue",
    timeframes: [
      { value: "this_fy", label: "This FY", key: "arr_churned_fy" },
    ],
  },
  {
    value: "pipeline",
    label: "Pipeline",
    description: "Open (non-closed) pipeline deal value",
    timeframes: [
      { value: "open", label: "Current open", key: "pipeline_value" },
    ],
  },
];

// Derive the backend KPI key from metric_type + timeframe
function deriveKey(metricType, timeframe) {
  const mt = METRIC_TYPES.find((t) => t.value === metricType);
  return mt?.timeframes.find((t) => t.value === timeframe)?.key ?? "current_mrr";
}

// ── BUILT_IN_KPIS ─────────────────────────────────────────────────────────────
// Kept for KpiGrid backwards-compatibility (existing saved visibleKpis).
// Not surfaced in the new Configure panel UI.

export const BUILT_IN_KPIS = [
  { key: "current_mrr",           label: "Current month MRR",                format: "currency" },
  { key: "arr_run_rate",          label: "ARR run rate",                      format: "currency" },
  { key: "arr_fy",                label: "ARR – this FY",                    format: "currency" },
  { key: "arr_last_fy",           label: "ARR – last FY",                    format: "currency" },
  { key: "arr_next_fy",           label: "ARR – next FY (projected)",        format: "currency" },
  { key: "arr_churned_fy",        label: "Churned ARR – this FY",            format: "currency" },
  { key: "closed_won_amount_fy",  label: "Closed won – this financial year",  format: "currency" },
  { key: "closed_won_amount_cy",  label: "Closed won – this calendar year",   format: "currency" },
  { key: "pipeline_value",        label: "Open pipeline value",               format: "currency" },
  { key: "active_deals",          label: "Active deals in view",              format: "number"   },
  { key: "products_in_view",      label: "Products in view",                  format: "number"   },
  { key: "open_ended_line_items", label: "Open-ended line items",             format: "number"   },
];

const OPERATORS = [
  { value: "+", label: "+" },
  { value: "-", label: "−" },
  { value: "*", label: "×" },
  { value: "/", label: "÷" },
];

const FORMATS = [
  { value: "currency", label: "Currency (£ / $ / €)" },
  { value: "number",   label: "Number"               },
];

export function normaliseVisibleKpis(visibleKpis, customMetrics) {
  const seen  = new Set(visibleKpis);
  const extra = (customMetrics ?? []).filter((m) => !seen.has(m.id)).map((m) => m.id);
  return extra.length ? [...visibleKpis, ...extra] : visibleKpis;
}

// ── SimpleMetricInput ─────────────────────────────────────────────────────────
// Three-step selector: type → timeframe → source (pipeline / stage).
// Used both as the standalone editor in "single value" mode and as the
// "By pipeline" sub-form inside formula operands.

function SimpleMetricInput({ value, onChange, pipelines = [], stagesByPipeline = {} }) {
  const metricType        = value.metric_type || "arr";
  const mt                = METRIC_TYPES.find((t) => t.value === metricType);
  const availableTimeframes = mt?.timeframes ?? [];
  const timeframe         = value.timeframe || (availableTimeframes[0]?.value ?? "");
  const selectedPipelines = value.pipelines || [];
  const selectedStages    = value.stages    || [];
  const [expandedPipelines, setExpandedPipelines] = useState({});

  const handleTypeChange = (newType) => {
    const newMt  = METRIC_TYPES.find((t) => t.value === newType);
    const newTf  = newMt?.timeframes[0]?.value ?? "";
    const newKey = newMt?.timeframes[0]?.key   ?? "current_mrr";
    onChange({ ...value, metric_type: newType, timeframe: newTf, key: newKey });
  };

  const handleTimeframeChange = (newTf) => {
    const newKey = mt?.timeframes.find((t) => t.value === newTf)?.key ?? value.key;
    onChange({ ...value, timeframe: newTf, key: newKey });
  };

  const togglePipeline = (name) => {
    const nowSelected = !selectedPipelines.includes(name);
    const nextPipes   = nowSelected
      ? [...selectedPipelines, name]
      : selectedPipelines.filter((p) => p !== name);
    let nextStages = selectedStages;
    if (!nowSelected) {
      const ps = new Set(stagesByPipeline[name] || []);
      nextStages = nextStages.filter((s) => !ps.has(s));
    }
    onChange({ ...value, pipelines: nextPipes, stages: nextStages });
  };

  const toggleStage = (stage) => {
    const next = selectedStages.includes(stage)
      ? selectedStages.filter((s) => s !== stage)
      : [...selectedStages, stage];
    onChange({ ...value, stages: next });
  };

  const toggleExpanded = (name) =>
    setExpandedPipelines((prev) => ({ ...prev, [name]: !prev[name] }));

  return (
    <div className="flex flex-col gap-4">

      {/* Step 1 – Type */}
      <div>
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">
          Type
        </p>
        <div className="flex flex-wrap gap-1.5">
          {METRIC_TYPES.map(({ value: tv, label, description }) => (
            <button
              key={tv}
              type="button"
              onClick={() => handleTypeChange(tv)}
              title={description}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                metricType === tv
                  ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                  : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {mt && (
          <p className="mt-1 text-[11px] leading-snug text-[#7d8b96]">{mt.description}</p>
        )}
      </div>

      {/* Step 2 – Timeframe */}
      {availableTimeframes.length > 1 ? (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">
            Timeframe
          </p>
          <div className="flex flex-wrap gap-1.5">
            {availableTimeframes.map(({ value: tv, label }) => (
              <button
                key={tv}
                type="button"
                onClick={() => handleTimeframeChange(tv)}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                  timeframe === tv
                    ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                    : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : availableTimeframes.length === 1 ? (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">
            Timeframe
          </p>
          <p className="text-xs text-[#4a5b68]">{availableTimeframes[0].label}</p>
        </div>
      ) : null}

      {/* Step 2b – Revenue basis (ARR / MRR only) */}
      {(metricType === "arr" || metricType === "mrr") && (
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">
            Revenue basis
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[["actual", "Actual (committed)"], ["projected", "Projected"]].map(([mv, label]) => (
              <button
                key={mv}
                type="button"
                onClick={() => onChange({ ...value, revenue_mode: mv })}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                  (value.revenue_mode || "actual") === mv
                    ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                    : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-[11px] leading-snug text-[#7d8b96]">
            Projected carries active renewals forward (with RPI) from the current month — use it for next-FY projected ARR.
          </p>
        </div>
      )}

      {/* Step 3 – Source (pipeline / stage) */}
      <div>
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4a5b68]">
          Source
          {selectedPipelines.length > 0
            ? ` · ${selectedPipelines.length} pipeline${selectedPipelines.length !== 1 ? "s" : ""} selected`
            : " (all pipelines if none selected)"}
        </p>
        {pipelines.length > 0 ? (
          <div className="max-h-56 overflow-y-auto rounded border border-[#dddddd] bg-[#f5f5f5] divide-y divide-[#dddddd]">
            {pipelines.map((name) => {
              const pipelineSelected = selectedPipelines.includes(name);
              const pipeStages       = stagesByPipeline[name] || [];
              const isExpanded       = expandedPipelines[name] ?? pipelineSelected;
              const checkedStages    = pipeStages.filter((s) => selectedStages.includes(s));
              return (
                <div key={name}>
                  <div className="flex items-center gap-2 px-2.5 py-2">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 flex-shrink-0 accent-[#50e3c2]"
                      checked={pipelineSelected}
                      onChange={() => togglePipeline(name)}
                    />
                    <span className="flex-1 text-xs font-medium text-[#323c44]">{name}</span>
                    {pipelineSelected && checkedStages.length > 0 && (
                      <span className="text-[10px] text-[#119996]">
                        {checkedStages.length} stage{checkedStages.length !== 1 ? "s" : ""}
                      </span>
                    )}
                    {pipeStages.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(name)}
                        className="text-[#7d8b96] hover:text-[#4a5b68] transition"
                        title={isExpanded ? "Collapse" : "Expand stages"}
                      >
                        <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {isExpanded && pipeStages.length > 0 && (
                    <div className="bg-white border-t border-[#dddddd] pl-7 pr-2.5 py-1.5 space-y-1">
                      {pipeStages.map((stage) => (
                        <label key={stage} className="flex cursor-pointer items-center gap-2 text-xs text-[#4a5b68]">
                          <input
                            type="checkbox"
                            className="h-3 w-3 accent-[#50e3c2]"
                            checked={selectedStages.includes(stage)}
                            onChange={() => toggleStage(stage)}
                          />
                          {stage}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-[11px] text-[#7d8b96] italic">
            No pipelines available — will use all data
          </p>
        )}
      </div>
    </div>
  );
}

// ── OperandInput (formula mode only) ─────────────────────────────────────────
// Offers three tabs: global built-in metric, fixed value, or "by pipeline"
// (which embeds SimpleMetricInput).

function OperandInput({ operand, onChange, pipelines = [], stagesByPipeline = {} }) {
  const mode          = operand?.type ?? "literal";
  const isMetric      = mode === "metric";
  const isLiteral     = mode === "literal";
  const isIndependent = mode === "independent_kpi";

  return (
    <div className="flex flex-col gap-2">
      {/* Tab selector */}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => onChange({ type: "metric", key: BUILT_IN_KPIS[0].key })}
          className={`flex-1 rounded border px-2 py-1.5 text-xs font-medium transition ${
            isMetric
              ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
              : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"
          }`}
        >
          Global metric
        </button>
        <button
          type="button"
          onClick={() => onChange({ type: "literal", value: 0 })}
          className={`flex-1 rounded border px-2 py-1.5 text-xs font-medium transition ${
            isLiteral
              ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
              : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"
          }`}
        >
          Fixed value
        </button>
        <button
          type="button"
          onClick={() =>
            onChange({
              type: "independent_kpi",
              metric_type: "arr",
              timeframe: "this_fy",
              key: "arr_fy",
              pipelines: [],
              stages: [],
            })
          }
          className={`flex-1 rounded border px-2 py-1.5 text-xs font-medium transition ${
            isIndependent
              ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
              : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"
          }`}
        >
          By pipeline
        </button>
      </div>

      {isMetric && (
        <select
          className="h-9 w-full rounded border border-[#dddddd] bg-[#f5f5f5] px-2 text-sm text-[#323c44]"
          value={operand.key}
          onChange={(e) => onChange({ type: "metric", key: e.target.value })}
        >
          {BUILT_IN_KPIS.map((m) => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>
      )}

      {isLiteral && (
        <input
          type="number"
          className="h-9 w-full rounded border border-[#dddddd] bg-[#f5f5f5] px-2 text-sm text-[#323c44]"
          placeholder="Enter value…"
          value={operand.value ?? ""}
          onChange={(e) => onChange({ type: "literal", value: Number(e.target.value) })}
        />
      )}

      {isIndependent && (
        <div className="rounded-md border border-[#dddddd] bg-[#f5f5f5] p-3">
          <SimpleMetricInput
            value={operand}
            onChange={onChange}
            pipelines={pipelines}
            stagesByPipeline={stagesByPipeline}
          />
        </div>
      )}
    </div>
  );
}

// ── ReorderButtons ────────────────────────────────────────────────────────────

function ReorderButtons({ onUp, onDown, disableUp, disableDown }) {
  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onUp}
        disabled={disableUp}
        className="rounded p-0.5 text-[#7d8b96] transition hover:text-[#4a5b68] disabled:opacity-25 disabled:cursor-not-allowed"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onDown}
        disabled={disableDown}
        className="rounded p-0.5 text-[#7d8b96] transition hover:text-[#4a5b68] disabled:opacity-25 disabled:cursor-not-allowed"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── Blank metric factory ──────────────────────────────────────────────────────

const BLANK_METRIC = () => ({
  id: Math.random().toString(36).slice(2),
  name: "",
  format: "currency",
  mode: "simple",
  operand_a: {
    type: "independent_kpi",
    metric_type: "arr",
    timeframe: "this_fy",
    key: "arr_fy",
    pipelines: [],
    stages: [],
  },
  invert_sign: false,
});

// ── Human-readable description of a metric (used in the list view) ───────────

function describeMetric(metric) {
  const operandStr = (op) => {
    if (!op) return "—";
    if (op.type === "metric") {
      return BUILT_IN_KPIS.find((m) => m.key === op.key)?.label ?? op.key;
    }
    if (op.type === "independent_kpi") {
      // New schema: metric_type + timeframe fields present
      if (op.metric_type) {
        const mt = METRIC_TYPES.find((t) => t.value === op.metric_type);
        const tf = mt?.timeframes.find((t) => t.value === op.timeframe);
        const parts = [];
        if (mt) parts.push(mt.label);
        if (tf) parts.push(tf.label);
        if ((op.pipelines || []).length) parts.push(op.pipelines.join(", "));
        else parts.push("all pipelines");
        return parts.join(" · ");
      }
      // Backwards compat: key only
      const kpiLabel = BUILT_IN_KPIS.find((m) => m.key === op.key)?.label ?? op.key;
      const pipeStr  = (op.pipelines || []).length ? ` [${op.pipelines.join(", ")}]` : "";
      return `${kpiLabel}${pipeStr}`;
    }
    return new Intl.NumberFormat("en-GB").format(op.value ?? 0);
  };

  if (metric.mode === "simple") return operandStr(metric.operand_a);

  const opLabel = OPERATORS.find((o) => o.value === metric.operator)?.label ?? metric.operator;
  return `${operandStr(metric.operand_a)} ${opLabel} ${operandStr(metric.operand_b)}`;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MetricsConfigPanel({
  open,
  onOpenChange,
  settings,
  onUpdate,
  filterOptions,
}) {
  const customMetrics    = settings.customMetrics ?? [];
  const stagesByPipeline = filterOptions?.stages_by_pipeline || {};
  const pipelines        = (filterOptions?.pipelines?.length)
    ? filterOptions.pipelines
    : Object.keys(stagesByPipeline);

  const visibleKpis = normaliseVisibleKpis(
    settings.visibleKpis ?? BUILT_IN_KPIS.map((k) => k.key),
    customMetrics,
  );

  const [addingMetric, setAddingMetric] = useState(false);
  const [editingId,    setEditingId]    = useState(null);
  const [draft,        setDraft]        = useState(BLANK_METRIC);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const resolveItem = (id) => {
    const custom = customMetrics.find((m) => m.id === id);
    if (custom) return { id: custom.id, label: custom.name, metric: custom };
    return null;
  };

  const visibleItems = visibleKpis.map(resolveItem).filter(Boolean);

  const moveItem = (id, dir) => {
    const idx = visibleKpis.indexOf(id);
    if (idx < 0) return;
    const next = [...visibleKpis];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onUpdate("visibleKpis", next);
  };

  const deleteCustom = (id) => {
    onUpdate("customMetrics", customMetrics.filter((m) => m.id !== id));
    onUpdate("visibleKpis",   visibleKpis.filter((k) => k !== id));
  };

  const startEdit = (metric) => {
    setDraft({ ...metric });
    setEditingId(metric.id);
    setAddingMetric(true);
  };

  const saveCustom = () => {
    if (!draft.name.trim()) return;
    const saved = { ...draft, name: draft.name.trim() };
    if (editingId) {
      onUpdate("customMetrics", customMetrics.map((m) => (m.id === editingId ? saved : m)));
    } else {
      onUpdate("customMetrics", [...customMetrics, saved]);
      onUpdate("visibleKpis",   [...visibleKpis, saved.id]);
    }
    setDraft(BLANK_METRIC());
    setAddingMetric(false);
    setEditingId(null);
  };

  const cancelAdd = () => {
    setDraft(BLANK_METRIC());
    setAddingMetric(false);
    setEditingId(null);
  };

  const draftMode = draft.mode ?? "formula";

  // Sheet is wider while editing so pipeline pickers have room.
  const sheetWidth = addingMetric ? "sm:max-w-2xl" : "sm:max-w-md";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={`flex w-full flex-col overflow-y-auto border-l border-[#cccccc] bg-white p-0 ${sheetWidth} transition-[max-width]`}
        data-testid="metrics-config-panel"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-[#dddddd] bg-[#f5f5f5] px-6 py-5">
          <SheetHeader>
            <SheetTitle className="font-sans text-xl text-[#323c44]">
              {addingMetric
                ? editingId ? "Edit metric" : "New metric"
                : "Configure metrics"}
            </SheetTitle>
            <SheetDescription className="text-sm text-[#4a5b68]">
              {addingMetric
                ? "Choose what to measure, the timeframe, and which pipeline to filter by."
                : "Manage your dashboard metrics — reorder, edit, add, or remove them."}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">

          {/* ══ Edit / Add form ══════════════════════════════════════════════ */}
          {addingMetric ? (
            <div className="space-y-6">

              {/* Metric name */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#4a5b68]">Metric name</label>
                <input
                  type="text"
                  className="h-9 w-full rounded border border-[#dddddd] bg-white px-3 text-sm text-[#323c44] focus:border-[#57c1a9] focus:outline-none"
                  placeholder="e.g. ARR – New Business"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  autoFocus
                />
              </div>

              {/* Mode: Single value vs Formula */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#4a5b68]">Metric type</label>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((d) => ({
                        ...d,
                        mode: "simple",
                        operand_a:
                          d.operand_a?.type === "independent_kpi"
                            ? d.operand_a
                            : {
                                type: "independent_kpi",
                                metric_type: "arr",
                                timeframe: "this_fy",
                                key: "arr_fy",
                                pipelines: [],
                                stages: [],
                              },
                      }))
                    }
                    className={`flex-1 rounded border px-3 py-2 text-xs font-medium transition ${
                      draftMode === "simple"
                        ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                        : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"
                    }`}
                  >
                    Single value
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft((d) => ({
                        ...d,
                        mode: "formula",
                        operator: d.operator ?? "-",
                        operand_b: d.operand_b ?? { type: "literal", value: 0 },
                      }))
                    }
                    className={`flex-1 rounded border px-3 py-2 text-xs font-medium transition ${
                      draftMode === "formula"
                        ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                        : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"
                    }`}
                  >
                    Formula (A ÷ B)
                  </button>
                </div>
                <p className="text-[11px] text-[#7d8b96]">
                  {draftMode === "simple"
                    ? "Display a single metric value — optionally filtered by pipeline or stage."
                    : "Compute a result from two values, e.g. Closed won − Target."}
                </p>
              </div>

              {/* Simple mode ─ type + timeframe + source */}
              {draftMode === "simple" && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-[#4a5b68]">What to measure</label>
                  <div className="rounded-md border border-[#dddddd] bg-[#f5f5f5] p-4">
                    <SimpleMetricInput
                      value={
                        draft.operand_a || {
                          type: "independent_kpi",
                          metric_type: "arr",
                          timeframe: "this_fy",
                          key: "arr_fy",
                          pipelines: [],
                          stages: [],
                        }
                      }
                      onChange={(v) => setDraft((d) => ({ ...d, operand_a: v }))}
                      pipelines={pipelines}
                      stagesByPipeline={stagesByPipeline}
                    />
                  </div>
                </div>
              )}

              {/* Formula mode ─ A op B */}
              {draftMode === "formula" && (
                <div className="grid gap-5 sm:grid-cols-[1fr_auto_1fr]">
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-[#4a5b68]">Value A</label>
                    <OperandInput
                      operand={draft.operand_a}
                      onChange={(v) => setDraft((d) => ({ ...d, operand_a: v }))}
                      pipelines={pipelines}
                      stagesByPipeline={stagesByPipeline}
                    />
                  </div>

                  <div className="flex flex-col items-center justify-start pt-6">
                    <select
                      className="h-9 w-14 rounded border border-[#dddddd] bg-white text-center text-lg font-bold text-[#323c44]"
                      value={draft.operator}
                      onChange={(e) => setDraft((d) => ({ ...d, operator: e.target.value }))}
                    >
                      {OPERATORS.map((op) => (
                        <option key={op.value} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-[#4a5b68]">Value B</label>
                    <OperandInput
                      operand={draft.operand_b}
                      onChange={(v) => setDraft((d) => ({ ...d, operand_b: v }))}
                      pipelines={pipelines}
                      stagesByPipeline={stagesByPipeline}
                    />
                  </div>
                </div>
              )}

              {/* Preview card */}
              {draft.name && (
                <div className="rounded-md border border-[#dddddd] bg-[#f5f5f5] px-4 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7d8b96]">Preview</p>
                  <p className="mt-1 text-sm font-medium text-[#323c44]">{draft.name}</p>
                  <p className="mt-0.5 text-xs text-[#4a5b68]">{describeMetric(draft)}</p>
                </div>
              )}

              {/* Display format */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#4a5b68]">Display format</label>
                <div className="flex gap-2">
                  {FORMATS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setDraft((d) => ({ ...d, format: value }))}
                      className={`flex-1 rounded border px-3 py-2 text-xs font-medium transition ${
                        draft.format === value
                          ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]"
                          : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Invert sign (formula mode only) */}
              {draftMode === "formula" && (
                <label className="flex cursor-pointer items-start gap-3 rounded-md border border-[#dddddd] bg-[#f5f5f5] px-4 py-3">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#50e3c2]"
                    checked={!!draft.invert_sign}
                    onChange={(e) => setDraft((d) => ({ ...d, invert_sign: e.target.checked }))}
                  />
                  <div>
                    <p className="text-xs font-semibold text-[#323c44]">Show surplus as positive</p>
                    <p className="mt-0.5 text-[11px] leading-snug text-[#7d8b96]">
                      When B exceeds A the result shows as{" "}
                      <span className="font-medium text-[#10b981]">+£value</span> (green).
                      Useful for gap / target metrics.
                    </p>
                  </div>
                </label>
              )}

              {/* Save / Cancel */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={saveCustom}
                  disabled={!draft.name.trim()}
                  className="flex-1 bg-[#50e3c2] text-[#323c44] hover:bg-[#57c1a9] disabled:opacity-50"
                >
                  {editingId ? "Update metric" : "Save metric"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelAdd}
                  className="border-[#dddddd] text-[#4a5b68]"
                >
                  Cancel
                </Button>
              </div>
            </div>

          ) : (
            /* ══ List view ══════════════════════════════════════════════════ */
            <div className="space-y-8">
              <section>
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">
                    Your metrics
                  </p>
                  <button
                    type="button"
                    onClick={() => { setEditingId(null); setDraft(BLANK_METRIC()); setAddingMetric(true); }}
                    className="flex items-center gap-1.5 rounded-md border border-[#dddddd] bg-white px-3 py-1.5 text-xs font-medium text-[#4a5b68] transition hover:bg-[#efefef]"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add metric
                  </button>
                </div>

                {/* Visible metrics */}
                {visibleItems.length > 0 ? (
                  <div className="mb-2 space-y-1.5">
                    {visibleItems.map(({ type, id, label, metric }, idx) => (
                      <div
                        key={id}
                        className="flex items-center gap-2 rounded-md border border-[#dddddd] bg-white px-3 py-2.5"
                      >
                        <GripVertical className="h-3.5 w-3.5 flex-shrink-0 text-[#cccccc]" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-[#323c44]">{label}</p>
                          {metric && (
                            <p className="mt-0.5 truncate text-xs text-[#7d8b96]">
                              {describeMetric(metric)}
                            </p>
                          )}
                        </div>
                        <ReorderButtons
                          onUp={() => moveItem(id, -1)}
                          onDown={() => moveItem(id, 1)}
                          disableUp={idx === 0}
                          disableDown={idx === visibleItems.length - 1}
                        />
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => startEdit(metric)}
                            className="flex-shrink-0 rounded p-0.5 text-[#7d8b96] transition hover:text-[#4a5b68]"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteCustom(id)}
                            className="flex-shrink-0 rounded p-0.5 text-[#7d8b96] transition hover:text-red-500"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-md border border-dashed border-[#dddddd] px-4 py-5 text-center text-sm text-[#7d8b96]">
                    No metrics visible. Click "Add metric" to create one.
                  </p>
                )}

              </section>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
